import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _dec, _class, _descriptor;
import { ClassUtils } from "abweb/utils/ClassUtils";
import { ManualPromise, AlreadyHandledError } from "abweb/utils/PromiseUtils";
import { intercept, makeObservable, observable, reaction, when } from "mobx";
export function createReactionName(name, fireImmediately, equals) {
  return {
    name: name,
    fireImmediately: !!fireImmediately,
    equals: equals
  };
}
export class MobXUtils {
  static interceptStringToNumber(target, propertyName) {
    return intercept(target, propertyName, MobXUtils.stringToNumberInterceptor);
  }

  /**
   * Call mobx 'when' that returns a promise. Mobx runs 'predicate' until it return true, then calls 'effect'. 'effect' must call resolve.
   * Registers disposer so mobx listener is cleaned up.
   * @param owner where the listener is registered
   * @param predicate function that refers to observables, and eventually returns true
   * @param effect callback made when promise resolves; passed a 'resolve' function it must call to continue the promise chain.
   */
  static promiseWhen(owner, predicate, effect) {
    return new Promise((resolve, reject) => {
      let registeredDisposer = null;
      // If first predicate evaluation is true, then 'effect' callback immediately executed
      // (which 'disposes' mobx listeners) so no need to subsequently register disposer
      let effectFired = false;
      const disposer = when(predicate, () => {
        effectFired = true;
        // after predicate becomes true, no need to destroy the listener any more
        registeredDisposer && owner.removeAsyncDestroyListener(registeredDisposer);
        // call effect, which calls resolve, which will make the next then in the chain get the resolved value.
        effect(resolve);
      }, {
        onError: err => {
          // CAT-1883: call disposer, unregister and reject
          disposer();
          registeredDisposer && owner.removeAsyncDestroyListener(registeredDisposer);
          reject(err);
        }
      });
      if (!effectFired) {
        // CAT-1884: if the reaction is disposed, reject the promise too
        registeredDisposer = owner.registerDestroyListener(() => {
          disposer();
          // even though disposer was called, the owner should be cleaned too
          registeredDisposer && owner.removeAsyncDestroyListener(registeredDisposer);
          reject(new AlreadyHandledError(Error("MobXUtils.promiseWhen: reaction was disposed before promise resolved")));
        });
      }
    });
  }

  /**
   * Call mobx 'when' that returns a promise. Mobx runs 'predicate' until it returns true. Registers disposer so mobx listener is cleaned up.
   * @param owner where the listener is registered
   * @param predicate function that refers to observables, and returns true for the promise to resolve
   * @param opts object with optional fields {name: string for debug, timeout: number of millis, onError: catch function of type (error) => void}
   */
  static when(owner, predicate, opts) {
    return new Promise((resolve, reject) => {
      let registeredDisposer = null;
      // If first predicate evaluation is true, then 'effect' callback immediately executed
      // (which 'disposes' mobx listeners) so no need to subsequently register disposer
      let effectFired = false;
      const disposer = when(predicate, () => {
        effectFired = true;
        // after predicate becomes true, no need to destroy the listener any more
        registeredDisposer && owner.removeAsyncDestroyListener(registeredDisposer);
        resolve();
      }, {
        ...opts,
        onError: err => {
          // CAT-1883: override client's onError to call disposer, unregister and reject
          disposer();
          registeredDisposer && owner.removeAsyncDestroyListener(registeredDisposer);
          opts?.onError && opts.onError(err);
          reject(err);
        }
      });
      if (!effectFired) {
        // CAT-1884: if the reaction is disposed, reject the promise too
        registeredDisposer = owner.registerDestroyListener(() => {
          disposer();
          // even though disposer was called, the owner should be cleaned too
          registeredDisposer && owner.removeAsyncDestroyListener(registeredDisposer);
          reject(new AlreadyHandledError(Error("MobXUtils.when: reaction was disposed before promise resolved")));
        });
      }
    });
  }

  /**
   * Returns a promise that allows you to await for a function whose result is mobx observable to return a result meeting some condition.
   *
   * @param resultFunction A function whose inputs are all observable and produces a result. This function will be called within a mobx reaction until
   *  the predicate approves the result of calling the function. The function will be re-run each time the value it observed change.
   *  The reaction and re-running of the function are also stopped when an exception is thrown.
   * @param condition A predicate that tests the result of calling resultFunction and returns true if the wait should complete, or false to continue waiting.
   * @return Promise that is fulfilled with the first result of calling resultFunction() that meets the condition. If either resultFunction() or condition() throw an
   * error the promise is rejected with the value of the thrown exception.
   */
  static waitUntilObservableResultMeetsCondition(resultFunction, condition) {
    let result;
    let promise = new ManualPromise();

    // this gets run over and over until condition is met or exception is thrown
    let whenPredicate = function () {
      // Run function and capture result
      result = resultFunction();
      // Test result to see if we are done with the when() reaction
      return condition(result);
    };

    // When condition returns true, this gets run
    let effect = function () {
      // fulfill the promise with the result of calling resultFunction() that met the condition
      promise.resolve(result);
    };

    // Called when an exception is thrown from whenPredicate
    let onError = function (error) {
      // Fail the promise with the exception value
      promise.reject(error);

      // Terminate the mobx when() reaction
      disposer();
    };

    // Run the whenPredicate until it returns true or throws an exception
    let disposer = when(whenPredicate, effect, {
      onError
    });

    // Synchronously return the promise (most likely not resolved) that we created and are prepared to resolve asynchronously.
    return promise.promise;
  }

  /**
   * Utility method to serve two purposes:
   * - Make the mobx reaction construct more findable using autocomplete
   * - Encourage proper cleanup practices
   * - Improved code readability by eliminating extra levels of parens and indentation.
   */
  static reaction(owner, expression, effect, options) {
    // console.log(":reaction"); //`
    owner.registerDestroyListener(reaction(expression, effect, options));
  }

  /**
   * Set the name property on an object.<br><br>
   * MobX's runInAction used to allow an optional "name" argument, but now only relies on properties.
   * Expected pattern:
   * ```
   * runInAction(MobXUtils.setName("actionName", fn));
   * ```
   */
  static setName(value, target) {
    return Object.defineProperty(target, "name", {
      writable: true,
      value: value
    });
  }
}

/**
 * Similar to observable.box, but this holds objects of any class.
 * The documentation for observable.box says it is designed to hold primitives, so we wrote our own.
 */
_defineProperty(MobXUtils, "stringToNumberInterceptor", change => {
  if (ClassUtils.isInstanceOf(change.newValue, String)) {
    change.newValue = parseInt(change.newValue, 10);
  }
  return change;
});
export let MobxBox = (_dec = observable.ref, _class = class MobxBox {
  constructor(val) {
    _initializerDefineProperty(this, "value", _descriptor, this);
    if (val) {
      this.value = val;
    }
    makeObservable(this);
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "value", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class);
//# sourceMappingURL=MobXUtils.js.map
